/**
 * Kosakata notifikasi — SATU sumber untuk seluruh aplikasi.
 *
 * ⛔ KENAPA BERKAS INI LAHIR. Sebelum 27 Agustus 2026, `namaJenisNotif`
 * tinggal di `pages/Dashboard.jsx` dan `pages/MasterNotificationChannels.jsx`
 * mengimpornya DARI HALAMAN ITU — halaman mengimpor halaman. Akibatnya
 * seluruh modul Dashboard (state, panggilan API, polling) ikut tertarik ke
 * graf ketergantungan layar saklar yang nol membutuhkannya.
 *
 * Saat kartu Tugas Saya perlu memakai `GRUP` yang tinggal di arah sebaliknya,
 * jalur itu akan menutup jadi LINGKARAN. Jadi keduanya diangkat ke sini.
 *
 * ⚠ Kalau kelak ada jenis notifikasi baru, tambahkan DI SINI saja. Dua
 * halaman langsung ikut — dan itu memang maksudnya.
 */

/**
 * Nama jenis notifikasi dalam bahasa manusia.
 *
 * ⚠ Layar sebelumnya menampilkan `SPECIAL_CUSTOMER_PENDING_APPROVAL` mentah
 * ke staf. Itu bahasa mesin: benar, dan nol berguna bagi orang yang harus
 * menindaklanjutinya. Jenis yang belum punya nama jatuh ke kodenya sendiri —
 * jelek, tapi jujur, dan lebih baik daripada kosong.
 */
const NAMA_JENIS = {
  DESIGN_TASK_NEW: "Task desain baru",
  DESIGN_TASK_CLAIMED: "Task desain diambil",
  DESIGN_REVISION_REQUESTED: "Revisi desain diminta",
  DESIGN_SPV_REJECTED: "Desain ditolak SPV",
  SPV_REVIEW_PENDING: "Menunggu review SPV",
  RECEIVABLE_OUTSTANDING: "Piutang berjalan",
  RECEIVABLE_OVERDUE: "Piutang lewat jatuh tempo",
  SPECIAL_CUSTOMER_PENDING_APPROVAL: "Special customer menunggu persetujuan",
  FOC_PENDING_APPROVAL: "FOC menunggu persetujuan",
  FA_PENDING_CUSTOMER_APPROVAL: "FA menunggu persetujuan customer",
  PAYMENT_PENDING_APPROVAL: "Pembayaran menunggu persetujuan",
  PR_PENDING_APPROVAL: "PR menunggu persetujuan",
  PACKING_TERTAHAN: "Packing tertahan (QC gagal)",
  PAYMENT_PENDING_FINANCE: "Menunggu eksekusi Finance",
  PAYMENT_REJECTED: "Pembayaran ditolak",
  PAYMENT_NOT_REQUESTED: "PO belum ditagihkan",
  PO_AUTO_COMPLETED: "PO selesai otomatis",
  // Aset & Maintenance (Tahap 4b-3; jenis lahir Tahap 2 DB, notifikasi Tahap 3)
  MAINTENANCE_JATUH_TEMPO: "Maintenance jatuh tempo",
  MAINTENANCE_TERLAMBAT: "Maintenance terlambat",
  LAPORAN_ASET_BULANAN: "Laporan bulanan aset",
  // Tugas harian K125 (U30, migration 20260921190000): dulu keduanya menumpang
  // DESIGN_TASK_NEW — kartunya bernama "Task desain baru" dan saklar desain
  // yang dimatikan ikut mematikannya (NOTIF-TAHAP-PRODUKSI-BERTIPE-…-01).
  TAHAP_TANPA_HADIR: "Tahap produksi tanpa yang hadir",
  TUGAS_TIDAK_TERKIRIM: "Tugas harian tidak terkirim",
  // Papan Kontrol K158 — jenis lahir bersama migration U24c (mendarat 22 Sep
  // 2026 malam); nama untuk jenis yang belum ada di CHECK DB dijaga merah oleh
  // test_notif_peta_rute.
  PAPAN_KONTROL_HARIAN: "Ringkasan harian Papan Kontrol",
  // Alarm INFRASTRUKTUR (K192, U36 — mendarat 23 Sep 2026): backup gagal, pemantau
  // kesehatan merah, deploy gagal, penjaga DOKU. Baris ini CATATANNYA; jalur yang
  // wajib sampai adalah Telegram, dan keduanya sengaja tidak saling bergantung.
  ALARM_INFRASTRUKTUR: "Alarm sistem",
  // K207 W5: AI WhatsApp meneruskan customer ke CS (komplain / tidak bisa dijawab) — dikerjakan dari layar Percakapan.
  WA_ESKALASI: "Customer WhatsApp perlu dibantu CS",
  // BUSINESS-100: WhatsApp penting (tautan bayar, Ruang Pesanan, FA, kwitansi, pesanan selesai) belum sampai.
  WA_BELUM_SAMPAI: "WhatsApp ke customer belum sampai",
  OTHER: "Lainnya",
};

export function namaJenisNotif(t) {
  return NAMA_JENIS[t] || t;
}

/**
 * Pengelompokan supaya enam belas jenis tidak terbaca sebagai satu daftar
 * panjang tanpa bentuk. Jenis yang tidak dikenal jatuh ke "Lainnya" — daftar
 * ini boleh tertinggal dari database tanpa menyembunyikan barisnya.
 *
 * ⚠ URUTAN PENTING: `cocok` diperiksa dari atas. "Persetujuan" sengaja
 * TERAKHIR karena polanya paling longgar (`includes("PENDING_APPROVAL")`) —
 * kalau ia naik, ia akan menelan `PAYMENT_PENDING_APPROVAL` yang seharusnya
 * masuk "Uang keluar".
 *
 * `rel` dan `ikon` dipakai kartu Tugas Saya untuk membedakan baris tanpa
 * menambah teks. Warnanya token, bukan literal — supaya ikut berbalik tema.
 */
export const GRUP = [
  {
    judul: "Desain",
    cocok: (t) => t.startsWith("DESIGN") || t.startsWith("SPV"),
    rel: "border-l-info",
    ikon: "Palette",
  },
  {
    judul: "Uang masuk",
    cocok: (t) => t.startsWith("RECEIVABLE"),
    rel: "border-l-success",
    ikon: "Wallet",
  },
  {
    judul: "Uang keluar",
    cocok: (t) => t.startsWith("PAYMENT") || t.startsWith("PO_"),
    rel: "border-l-warning",
    ikon: "Banknote",
  },
  {
    // L7 (Owner, 16 Sep 2026): nama grup "Maintenance". Dua awalan karena
    // jenis laporan bulanan aset nol berawalan MAINTENANCE_.
    judul: "Maintenance",
    cocok: (t) => t.startsWith("MAINTENANCE_") || t === "LAPORAN_ASET_BULANAN",
    rel: "border-l-neutral",
    ikon: "Wrench",
  },
  {
    // Tugas harian K125 (U30): kabar tahap produksi — bukan Desain. Rute
    // kartunya tetap dari `related_entity_type` (production_stage → /personil,
    // wo_stage_task → /wo/{id}) di `routeForNotif`.
    judul: "Produksi",
    cocok: (t) => t === "TAHAP_TANPA_HADIR" || t === "TUGAS_TIDAK_TERKIRIM" || t === "PACKING_TERTAHAN",
    rel: "border-l-warning",
    ikon: "Factory",
  },
  {
    // Papan Kontrol K158 (U24c): ringkasan harian MERAH/KUNING per region untuk
    // Owner + KC — rute kartunya ke /papan-kontrol lewat related_entity_type.
    judul: "Papan Kontrol",
    cocok: (t) => t === "PAPAN_KONTROL_HARIAN",
    rel: "border-l-danger",
    ikon: "Gauge",
  },
  {
    // K192: alarm infrastruktur berdiri SENDIRI di lonceng — ia bukan pekerjaan
    // yang menunggu orang, melainkan kabar bahwa sesuatu di bawah sistem rusak.
    judul: "Alarm sistem",
    cocok: (t) => t === "ALARM_INFRASTRUKTUR",
    rel: "border-l-danger",
    ikon: "TriangleAlert",
  },
  {
    // K207 W5: eskalasi AI WhatsApp — customer menunggu dibalas manusia di Cekat.
    judul: "Customer WhatsApp",
    cocok: (t) => t === "WA_ESKALASI" || t === "WA_BELUM_SAMPAI",
    rel: "border-l-danger",
    ikon: "TriangleAlert",
  },
  {
    judul: "Persetujuan",
    cocok: (t) => t.includes("PENDING_APPROVAL") || t.startsWith("FA_"),
    rel: "border-l-danger",
    ikon: "ShieldCheck",
  },
];

const LAINNYA = { judul: "Lainnya", rel: "border-l-border", ikon: "Circle" };

export function grupUntuk(type) {
  return GRUP.find((x) => x.cocok(type)) || LAINNYA;
}

/** Nama grup saja — bentuk lama, dipertahankan supaya pemanggil tidak patah. */
export function namaGrup(type) {
  return grupUntuk(type).judul;
}

/**
 * Umur tugas dalam hari penuh.
 *
 * ⚠ Dihitung dari BATAS HARI, bukan selisih 24 jam. Tugas yang masuk kemarin
 * pukul 23.00 sudah "1 hari menunggu" pagi ini — dan itu yang dirasakan orang
 * yang menungguinya, bukan "0 hari" yang benar secara aritmetika.
 */
export function umurHari(created_at) {
  if (!created_at) return null;
  const lahir = new Date(created_at);
  if (Number.isNaN(lahir.getTime())) return null;
  const a = new Date(lahir); a.setHours(0, 0, 0, 0);
  const b = new Date(); b.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((b - a) / 86400000));
}

/**
 * Keadaan eskalasi satu tugas, dari `data.eskalasi` yang ditulis
 * `services/eskalasi_service.py`.
 *
 * ⛔ KENAPA INI PERLU TERLIHAT. Mesin eskalasi berjalan tiap pagi sejak
 * 28 Agustus 2026 dan MELEBARKAN `target_role_codes` — tugas yang tertahan
 * naik ke Kepala Cabang, lalu ke Owner. Tapi di layar, tugas yang sudah
 * dinaikkan tiga kali terlihat SAMA PERSIS dengan yang baru masuk pagi ini.
 *
 * Akibatnya bukan kosmetik: orang yang membuka daftar tugas nol punya cara
 * tahu mana yang sudah jadi masalah bersama. Mesin bekerja, dan hasil
 * kerjanya tidak sampai ke mata siapa pun.
 *
 * ⚠ MENGEMBALIKAN SATU penanda, bukan seluruh riwayat. Tugas berumur 8 hari
 * kerja bisa punya tiga penanda sekaligus; menampilkan ketiganya membuat
 * baris jadi penuh label dan yang penting tenggelam. Yang ditampilkan yang
 * PALING JAUH — karena itu yang mengubah siapa harus bertindak.
 */
const _URUTAN_ESKALASI = [
  // K116 piutang H+7 — anak tangga terakhir tangga piutang (sesudah naik ke KC).
  { awalan: "perlu_tindak_lanjut", label: "Perlu tindak lanjut", berat: true },
  { awalan: "naik_owner", label: "Sudah naik ke Owner", berat: true },
  { awalan: "naik_kacab", label: "Sudah naik ke Kepala Cabang", berat: true },
  { awalan: "tertahan_lama", label: "Tertahan lama", berat: true },
  { awalan: "diingatkan", label: "Sudah diingatkan", berat: false },
];

export function eskalasiTerjauh(notif) {
  const esk = notif?.data?.eskalasi;
  if (!esk || typeof esk !== "object") return null;
  const penanda = Object.keys(esk);
  if (penanda.length === 0) return null;
  // ⚠ Dicocokkan lewat AKHIRAN, bukan daftar nama lengkap. Penanda berbentuk
  // `h1_naik_kacab`, `h3_naik_owner`, `h7_tertahan_lama` — angka di depannya
  // adalah ambang hari kerja, dan ambang itu boleh berubah lewat keputusan
  // Owner tanpa artinya berubah. Daftar nama lengkap akan diam-diam berhenti
  // cocok pada hari seseorang menggeser H+3 jadi H+4.
  for (const tingkat of _URUTAN_ESKALASI) {
    if (penanda.some((p) => p.endsWith(tingkat.awalan))) {
      return { ...tingkat, jumlah: penanda.length };
    }
  }
  // Penanda yang tidak dikenal TETAP dilaporkan, bukan disembunyikan: tangga
  // baru yang lahir tanpa label di sini harus terlihat sebagai "ada sesuatu",
  // bukan lenyap.
  return { awalan: null, label: "Sudah dieskalasi", berat: false,
           jumlah: penanda.length };
}
